import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox';
import { H2 } from '@/components/atoms/Typography/Typography';
import { CloudinaryImage } from '@/components/local/CloudinaryImage/CloudinaryImage';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import {
  formFields,
  FormValues,
} from '@/containers/ConfigurePage/components/FormConfig/formData';
import { useData } from '@/lib/pageData/pageDataContext';
import { formatPrice } from '@/utils/formatPrice';
import styles from './BasketAddon.module.scss';

interface Props {
  imageSrc: string;
  price: number;
  onChange: (isChecked: boolean) => void;
}

export const BasketAddon = ({ imageSrc, price, onChange }: Props) => {
  const { locale } = useRouter();
  const { currency } = useData<'default'>();
  const { t } = useTranslation([
    nameSpacesConfig.common,
    nameSpacesConfig.pages,
  ]);
  const { control } = useFormContext<FormValues>();

  return (
    <div className={styles.container}>
      <H2 version="level3" className={styles.heading}>
        {t('pages:configure.basket.heading')}
      </H2>
      <p className={styles.description}>{t('pages:configure.basket.text')}</p>
      <Controller
        control={control}
        name={formFields.basketAddon}
        render={({ field }) => (
          <Checkbox
            className={styles.checkboxContainer}
            value={!!field.value}
            onChange={isChecked => {
              onChange(isChecked);
              field.onChange(isChecked);
            }}
            aria-label={t('pages:configure.basket.label')}
            variant="outlined"
          >
            <div className={styles.checkboxContent}>
              <span className={styles.label}>
                {t('pages:configure.basket.label')}
              </span>
              <span translate="no">
                +
                {t('common:pricePerMonth', {
                  price: formatPrice(price, locale!, currency),
                })}
              </span>
            </div>
            <CloudinaryImage
              src={imageSrc!}
              alt={t('pages:configure.image.alt')}
              layout="fill"
              objectFit="contain"
              objectPosition="100%"
            />
          </Checkbox>
        )}
      />
    </div>
  );
};
